import React, { useState, useEffect, useRef } from "react";
import FilterSection from "../components/FilterSection";
import OrderCard from "../components/OrderCard";
import OrderForm from "../components/OrderForm";
import {
  getAllOrders,
  addOrderToSheet,
  updateOrderInSheet,
  deleteOrderFromSheet,
} from "../assets/services/googleSheetsService";
import {
  reduceStockForOrder,
  restoreStockForDeletedOrder,
  adjustStockForUpdatedOrder,
} from "../assets/services/stockService";
import {
  getPriceForDate,
  getPriceHistory,
} from "../assets/services/priceHistoryService";
import { getAllOrderNotes } from "../assets/services/notesService";
import { getAllProductsFromSheet } from "../assets/services/productService";

type StatusType =
  | "All"
  | "Preparing"
  | "Packed"
  | "Shipped"
  | "Dispatched"
  | "Delivered"
  | "Reschedule"
  | "Return"
  | "Transfer"
  | "Damaged"
  | "No Answer";
type ProductType =
  | "All"
  | "Oil"
  | "Shampoo"
  | "Conditioner"
  | "Spray"
  | "Serum"
  | "Premium"
  | "Castor"
  | "Rosehip"
  | "Beard"
  | "Oil (Refill)";
type PaymentStatusType = "All" | "COD Paid" | "COD Unpaid" | "Bank Transfer";

interface Order {
  name: string;
  addressLine1: string;
  addressLine2?: string;
  addressLine3?: string;
  mainCity?: string; // Q (16)
  contact: string;
  products: {
    name: string;
    quantity: number;
    price: number;
  }[];
  status:
    | "Preparing"
    | "Packed"
    | "Shipped"
    | "Dispatched"
    | "Delivered"
    | "Reschedule"
    | "Return"
    | "Transfer"
    | "Damaged"
    | "No Answer";
  orderDate: string;
  paymentMethod: "COD" | "Bank Transfer";
  paymentReceived?: boolean;
  tracking?: string;
  freeShipping?: boolean;
  lastUpdated?: string;
  fdeStatus?: string; // R (17): FDE waybill number
  notes?: string; // S (18): order notes
}

const Orders: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [trackingSearch, setTrackingSearch] = useState("");
  const [selectedStatus, setSelectedStatus] = useState<StatusType[]>(["All"]);
  const [selectedProduct, setSelectedProduct] = useState<ProductType[]>([
    "All",
  ]);
  const [selectedPaymentStatus, setSelectedPaymentStatus] = useState<
    PaymentStatusType[]
  >(["All"]);
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [showDateFilter, setShowDateFilter] = useState(false);

  const [itemsPerPage, setItemsPerPage] = useState(100);
  const [currentPage, setCurrentPage] = useState(1);

  const [showOrderForm, setShowOrderForm] = useState(false);
  const [editingOrder, setEditingOrder] = useState<Order | null>(null);
  const [formMode, setFormMode] = useState<"create" | "update">("create");

  const fileInputRef = useRef<HTMLInputElement>(null);

  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadOrdersFromSheets();
  }, []);

  const loadOrdersFromSheets = async () => {
    try {
      setLoading(true);
      setError(null);

      const [result, noteMap, historyResult, productsResult] =
        await Promise.all([
          getAllOrders(),
          getAllOrderNotes(),
          getPriceHistory(),
          getAllProductsFromSheet(),
        ]);

      const livePrices: Record<string, number> = {};
      if (productsResult.success && productsResult.data) {
        productsResult.data.forEach((p) => {
          livePrices[p.name] = p.price;
        });
      }

      // Resolve a product's price for this order: prefer the price that was
      // actually active on the order's date (Price_History), then the
      // current live price from the Products sheet, then the hardcoded
      // constant as a last-resort safety net.
      const resolvePrice = (
        productName: string,
        orderDate: string,
        hardcodedFallback: number,
      ) =>
        getPriceForDate(historyResult, productName, orderDate) ||
        livePrices[productName] ||
        hardcodedFallback;

      if (result.success && result.data) {
        const convertedOrders: Order[] = result.data.map((sheetOrder) => {
          const customerLines = sheetOrder.customerInfo.split("\n");
          const name = customerLines[0] || "";
          const address = customerLines.slice(1, -1).join(", ") || "";
          const contact = customerLines[customerLines.length - 1] || "";

          const products = [];
          const orderDate = sheetOrder.orderDate;

          if (sheetOrder.oilQty > 0) {
            products.push({
              name: "Oil",
              quantity: sheetOrder.oilQty,
              price: resolvePrice("Oil", orderDate, 950),
            });
          }
          if (sheetOrder.shampooQty > 0) {
            products.push({
              name: "Shampoo",
              quantity: sheetOrder.shampooQty,
              price: resolvePrice("Shampoo", orderDate, 1350),
            });
          }
          if (sheetOrder.conditionerQty > 0) {
            products.push({
              name: "Conditioner",
              quantity: sheetOrder.conditionerQty,
              price: resolvePrice("Conditioner", orderDate, 1350),
            });
          }
          if (sheetOrder.sprayQty > 0) {
            products.push({
              name: "Spray",
              quantity: sheetOrder.sprayQty,
              price: resolvePrice("Spray", orderDate, 980),
            });
          }
          if (sheetOrder.serumQty > 0) {
            products.push({
              name: "Serum",
              quantity: sheetOrder.serumQty,
              price: resolvePrice("Serum", orderDate, 1600),
            });
          }
          if (sheetOrder.premiumQty > 0) {
            products.push({
              name: "Premium",
              quantity: sheetOrder.premiumQty,
              price: resolvePrice("Premium", orderDate, 2600),
            });
          }
          if (sheetOrder.castorQty > 0) {
            products.push({
              name: "Castor",
              quantity: sheetOrder.castorQty,
              price: resolvePrice("Castor", orderDate, 2400),
            });
          }
          if (sheetOrder.rosehipQty > 0) {
            products.push({
              name: "Rosehip",
              quantity: sheetOrder.rosehipQty,
              price: resolvePrice("Rosehip", orderDate, 2950),
            });
          }
          if (sheetOrder.beardQty > 0) {
            products.push({
              name: "Beard",
              quantity: sheetOrder.beardQty,
              price: resolvePrice("Beard", orderDate, 1200),
            });
          }
          if (sheetOrder.refillQty > 0) {
            products.push({
              name: "Oil (Refill)",
              quantity: sheetOrder.refillQty,
              price: resolvePrice("Oil (Refill)", orderDate, 2900),
            });
          }

          return {
            name,
            addressLine1: address,
            addressLine2: "",
            addressLine3: "",
            mainCity: sheetOrder.mainCity || "", // Q (16)
            contact,
            products,
            status: sheetOrder.orderStatus as Order["status"],
            orderDate: sheetOrder.orderDate,
            paymentMethod: sheetOrder.paymentMethod as Order["paymentMethod"],
            paymentReceived: sheetOrder.paymentReceived,
            tracking: sheetOrder.trackingId,
            freeShipping: sheetOrder.freeShipping,
            lastUpdated: sheetOrder.lastUpdated,
            fdeStatus: sheetOrder.fdeStatus || "", // R (17): makes FDE button persistent
            notes: noteMap[sheetOrder.trackingId] || "", // S (18): order notes
          };
        });

        setOrders(convertedOrders);
      } else {
        setError(result.error || "Failed to load orders");
      }
    } catch (err) {
      console.error("Error loading orders:", err);
      setError("An unexpected error occurred while loading orders");
    } finally {
      setLoading(false);
    }
  };

  const parseOrderDate = (dateString: string): number => {
    if (!dateString) return 0;
    let parsedDate: Date;
    if (dateString.includes("T") || dateString.includes("-")) {
      parsedDate = new Date(dateString);
    } else {
      parsedDate = new Date(dateString);
    }
    return isNaN(parsedDate.getTime()) ? 0 : parsedDate.getTime();
  };

  const filteredOrders = orders.filter((order) => {
    const matchesGeneral =
      searchTerm === "" ||
      order.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.addressLine1.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.addressLine2?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.addressLine3?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.contact.includes(searchTerm);

    const matchesTracking =
      trackingSearch === "" ||
      order.tracking?.toLowerCase().includes(trackingSearch.toLowerCase());

    const matchesStatus =
      selectedStatus.includes("All") || selectedStatus.includes(order.status);

    const matchesProduct =
      selectedProduct.includes("All") ||
      order.products.some((product) =>
        selectedProduct.includes(product.name as ProductType),
      );

    const matchesPaymentStatus =
      selectedPaymentStatus.includes("All") ||
      (selectedPaymentStatus.includes("COD Paid") &&
        order.paymentMethod === "COD" &&
        order.paymentReceived) ||
      (selectedPaymentStatus.includes("COD Unpaid") &&
        order.paymentMethod === "COD" &&
        !order.paymentReceived) ||
      (selectedPaymentStatus.includes("Bank Transfer") &&
        order.paymentMethod === "Bank Transfer");

    const orderDate = new Date(order.orderDate);
    const matchesDateRange =
      (!startDate || orderDate >= new Date(startDate)) &&
      (!endDate || orderDate <= new Date(endDate));

    return (
      matchesGeneral &&
      matchesTracking &&
      matchesStatus &&
      matchesProduct &&
      matchesPaymentStatus &&
      matchesDateRange
    );
  });

  const sortedOrders = filteredOrders.sort((a, b) => {
    const aOrderTime = parseOrderDate(a.orderDate);
    const bOrderTime = parseOrderDate(b.orderDate);
    if (aOrderTime !== bOrderTime) {
      return bOrderTime - aOrderTime;
    }
    if (a.tracking && b.tracking) {
      return b.tracking.localeCompare(a.tracking);
    }
    return a.name.localeCompare(b.name);
  });

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedOrders = sortedOrders.slice(startIndex, endIndex);

  const clearFilters = () => {
    setSearchTerm("");
    setTrackingSearch("");
    setSelectedStatus(["All"]);
    setSelectedProduct(["All"]);
    setSelectedPaymentStatus(["All"]);
    setStartDate("");
    setEndDate("");
    setShowDateFilter(false);
    setCurrentPage(1);
  };

  const handleCreateOrder = () => {
    setFormMode("create");
    setEditingOrder(null);
    setShowOrderForm(true);
  };

  const handleUpdateOrder = (order: Order) => {
    setFormMode("update");
    setEditingOrder(order);
    setShowOrderForm(true);
  };

  const handleOrderSubmit = async (orderData: Order) => {
    try {
      if (formMode === "create") {
        const result = await addOrderToSheet(orderData);

        if (result.success) {
          try {
            const stockReductionResult = await reduceStockForOrder(
              orderData.products.map((product) => ({
                name: product.name,
                quantity: product.quantity,
              })),
            );

            if (stockReductionResult.success) {
              console.log("Stock reduced successfully for new order");
              alert(
                `Order created successfully! Tracking ID: ${result.trackingId}\nStock has been updated automatically.`,
              );
            } else {
              console.error(
                "Failed to reduce stock:",
                stockReductionResult.error,
              );
              alert(
                `Order created successfully! Tracking ID: ${result.trackingId}\nWarning: Failed to update stock - ${stockReductionResult.error}`,
              );
            }
          } catch (stockError) {
            console.error("Error reducing stock:", stockError);
            alert(
              `Order created successfully! Tracking ID: ${result.trackingId}\nWarning: Failed to update stock automatically.`,
            );
          }

          await loadOrdersFromSheets();
        } else {
          alert(`Error creating order: ${result.error}`);
        }
      } else {
        if (editingOrder?.tracking) {
          try {
            const result = await updateOrderInSheet(
              editingOrder.tracking,
              orderData,
            );

            if (result.success) {
              try {
                const oldProducts = editingOrder.products.map((product) => ({
                  name: product.name,
                  quantity: product.quantity,
                }));

                const newProducts = orderData.products.map((product) => ({
                  name: product.name,
                  quantity: product.quantity,
                }));

                const stockAdjustmentResult = await adjustStockForUpdatedOrder(
                  oldProducts,
                  newProducts,
                );

                if (stockAdjustmentResult.success) {
                  console.log("Stock adjusted successfully for updated order");
                  alert("Order and stock updated successfully!");
                } else {
                  console.error(
                    "Failed to adjust stock:",
                    stockAdjustmentResult.error,
                  );
                  alert(
                    `Order updated successfully!\nWarning: Failed to adjust stock - ${stockAdjustmentResult.error}`,
                  );
                }
              } catch (stockError) {
                console.error("Error adjusting stock:", stockError);
                alert(
                  "Order updated successfully!\nWarning: Failed to adjust stock automatically.",
                );
              }

              await loadOrdersFromSheets();
            } else {
              alert(`Error updating order: ${result.error}`);
            }
          } catch (error) {
            console.error("Error updating order:", error);
            alert("Failed to update order. Please try again.");
          }
        }
      }
    } catch (error) {
      console.error("Error submitting order:", error);
      alert("An unexpected error occurred. Please try again.");
    }

    setShowOrderForm(false);
    setEditingOrder(null);
  };

  const handleOrderDelete = async () => {
    if (!editingOrder?.tracking) return;

    if (
      !window.confirm(
        `Are you sure you want to delete order ${editingOrder.tracking}?`,
      )
    ) {
      return;
    }

    try {
      const result = await deleteOrderFromSheet(editingOrder.tracking);

      if (result.success) {
        try {
          const stockRestorationResult = await restoreStockForDeletedOrder(
            editingOrder.products.map((product) => ({
              name: product.name,
              quantity: product.quantity,
            })),
          );

          if (stockRestorationResult.success) {
            console.log("Stock restored successfully for deleted order");
            alert("Order deleted and stock restored successfully!");
          } else {
            console.error(
              "Failed to restore stock:",
              stockRestorationResult.error,
            );
            alert(
              `Order deleted successfully!\nWarning: Failed to restore stock - ${stockRestorationResult.error}`,
            );
          }
        } catch (stockError) {
          console.error("Error restoring stock:", stockError);
          alert(
            "Order deleted successfully!\nWarning: Failed to restore stock automatically.",
          );
        }

        setShowOrderForm(false);
        setEditingOrder(null);
        await loadOrdersFromSheets();
      } else {
        alert(`Error deleting order: ${result.error}`);
      }
    } catch (error) {
      console.error("Error deleting order:", error);
      alert("An unexpected error occurred while deleting the order.");
    }
  };

  const handleDeleteOrderFromCard = async (order: Order) => {
    if (!order.tracking) return;

    try {
      const result = await deleteOrderFromSheet(order.tracking);

      if (result.success) {
        try {
          const stockRestorationResult = await restoreStockForDeletedOrder(
            order.products.map((product) => ({
              name: product.name,
              quantity: product.quantity,
            })),
          );

          if (stockRestorationResult.success) {
            console.log("Stock restored successfully for deleted order");
            alert("Order deleted and stock restored successfully!");
          } else {
            console.error(
              "Failed to restore stock:",
              stockRestorationResult.error,
            );
            alert(
              `Order deleted successfully!\nWarning: Failed to restore stock - ${stockRestorationResult.error}`,
            );
          }
        } catch (stockError) {
          console.error("Error restoring stock:", stockError);
          alert(
            "Order deleted successfully!\nWarning: Failed to restore stock automatically.",
          );
        }

        await loadOrdersFromSheets();
      } else {
        alert(`Error deleting order: ${result.error}`);
      }
    } catch (error) {
      console.error("Error deleting order:", error);
      alert("An unexpected error occurred while deleting the order.");
    }
  };

  const hasDateFilter = startDate || endDate;

  if (loading) {
    return (
      <div className="container px-4 py-8 mx-auto">
        <div className="flex items-center justify-center min-h-64">
          <div className="text-center">
            <div className="w-12 h-12 mx-auto border-b-2 rounded-full animate-spin border-primary"></div>
            <p className="mt-4 text-gray-600">Loading orders...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container px-4 py-8 mx-auto">
        <div className="flex items-center justify-center min-h-64">
          <div className="text-center">
            <div className="mb-4 text-xl text-red-500">⚠️</div>
            <h2 className="mb-2 text-xl font-semibold text-gray-900">
              Error Loading Orders
            </h2>
            <p className="mb-4 text-gray-600">{error}</p>
            <button
              onClick={loadOrdersFromSheets}
              className="px-4 py-2 text-white transition-colors rounded-lg bg-primary hover:bg-primary/90"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container pt-0 mx-auto">
      <div className="flex items-center justify-between sticky z-10 top-16 md:top-20 pb-4 bg-[#F9FAFB] gap-2">
        <div>
          <h1 className="text-xl font-bold text-gray-800 md:text-2xl">
            Orders Dashboard
          </h1>
        </div>
        <button
          onClick={handleCreateOrder}
          className="flex items-center px-4 py-2 space-x-2 font-medium text-white transition-colors rounded-lg bg-primary hover:bg-primary/90"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 4v16m8-8H4"
            />
          </svg>
          <span>New Order</span>
        </button>
      </div>

      <FilterSection
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        trackingSearch={trackingSearch}
        setTrackingSearch={setTrackingSearch}
        selectedStatus={selectedStatus}
        setSelectedStatus={setSelectedStatus}
        selectedProduct={selectedProduct}
        setSelectedProduct={setSelectedProduct}
        selectedPaymentStatus={selectedPaymentStatus}
        setSelectedPaymentStatus={setSelectedPaymentStatus}
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        showDateFilter={showDateFilter}
        setShowDateFilter={setShowDateFilter}
        clearFilters={clearFilters}
        filteredOrdersLength={filteredOrders.length}
        totalOrdersLength={orders.length}
        itemsPerPage={itemsPerPage}
        setItemsPerPage={setItemsPerPage}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
      />

      {/* Orders List */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
        <div className="h-[600px] overflow-y-auto p-4 space-y-4">
          {paginatedOrders.map((order, index) => (
            <OrderCard
              key={`${order.tracking}-${index}`}
              order={order}
              onUpdateClick={handleUpdateOrder}
              onDeleteClick={handleDeleteOrderFromCard}
            />
          ))}
        </div>
      </div>

      {filteredOrders.length === 0 &&
        (searchTerm ||
          trackingSearch ||
          !selectedStatus.includes("All") ||
          !selectedProduct.includes("All") ||
          !selectedPaymentStatus.includes("All") ||
          hasDateFilter) && (
          <div className="py-8 text-center">
            <p className="text-gray-500">
              No orders found matching your search criteria.
            </p>
          </div>
        )}

      {/* Empty state */}
      {orders.length === 0 && !loading && !error && (
        <div className="py-8 text-center">
          <div className="mb-4 text-6xl text-gray-400">📦</div>
          <h2 className="mb-2 text-xl font-semibold text-gray-900">
            No Orders Found
          </h2>
          <p className="mb-6 text-gray-600">
            Get started by creating your first order
          </p>
          <button
            onClick={handleCreateOrder}
            className="px-6 py-3 text-white transition-colors rounded-lg bg-primary hover:bg-primary/90"
          >
            Create First Order
          </button>
        </div>
      )}

      <OrderForm
        isOpen={showOrderForm}
        onClose={() => setShowOrderForm(false)}
        onDelete={handleOrderDelete}
        onSubmit={handleOrderSubmit}
        initialOrder={editingOrder}
        mode={formMode}
      />
    </div>
  );
};

export default Orders;
